import { createContext, useContext, useMemo, type ComponentType } from "react";
import type { KapselKennzahl, KapselReiter, Modul, UnterlageVorschauProps } from "./modul";
import type { Recht } from "./rechte";
import { useAngemeldet } from "./sitzung";

/** Art eines Vorhabens (BAUPLAN-VORHABEN-ARTEN.md, Abschnitt 2, ADR-024). Steuert die Reiter der Kapsel: Ein Reiter oder eine Kennzahl
 *  mit `arten` gilt nur für diese Arten. Eine weitere Art ist ein neuer Schlüssel und ein `arten`-Eintrag an den Reitern. Die Texte dazu
 *  liegen im Modul aufgaben. */
export type VorhabenArt = "veranstaltung" | "foerderantrag" | "projekt";
export const VORHABEN_ARTEN: readonly VorhabenArt[] = ["veranstaltung", "foerderantrag", "projekt"];

/** Was ein Reiter über die Kapsel wissen darf, um zu entscheiden, ob er sich zeigt. Die Seite des Vorhabens füllt es aus. */
export interface KapselLage {
  vorhabenId: string;
  /** Name des Vorhabens, zum Beispiel für Beschriftungen und Dateinamen */
  name: string;
  /** Rolle der angemeldeten Person im Vorhaben. Der Vorstand sieht Kapseln auch ohne Rolle (null). */
  meineRolle: "leitend" | "mitwirkend" | null;
  /** false, wenn das Vorhaben archiviert ist */
  aktiv: boolean;
  /** Art des Vorhabens. Fehlt die Spalte noch (Minuten nach dem Deploy), ist es eine Veranstaltung. */
  art: VorhabenArt;
}

/** Alle Kapsel-Reiter der Module, ungefiltert. Die Shell stellt sie bereit, die Seite des Vorhabens filtert mit useKapselReiter. */
export const KapselReiterKontext = createContext<readonly KapselReiter[]>([]);

/** Reiter aller Module in einer Liste (BAUPLAN-BUDGET.md, Abschnitt 5.1). */
export function kapselReiterAus(module: readonly Modul[]): KapselReiter[] {
  return module.flatMap((m) => m.kapselReiter ?? []);
}

/** Was Reiter und Kennzahlen gemeinsam haben, um zu entscheiden, ob sie sich zeigen */
interface InKapsel {
  label: string;
  /** Nur für diese Arten. Ohne: für jede Art. */
  arten?: readonly VorhabenArt[];
  recht?: Recht;
  pruefen?: (lage: KapselLage) => boolean;
  reihenfolge?: number;
}

/** Zuerst nach Art (Abschnitt 2), dann sichtbar mit dem Recht oder wenn die Prüfung zutrifft. Ohne beides sieht jede Person der Kapsel
 *  den Reiter. Sortiert nach reihenfolge (Standard 50), dann nach Label. Gilt genauso für Kennzahlen. */
export function kapselReiterFuer<T extends InKapsel>(reiter: readonly T[], rechte: ReadonlySet<Recht>, lage: KapselLage): T[] {
  return reiter
    .filter((r) => !r.arten || r.arten.includes(lage.art))
    .filter((r) => {
      if (!r.recht && !r.pruefen) return true;
      return (!!r.recht && rechte.has(r.recht)) || (r.pruefen?.(lage) ?? false);
    })
    .sort((a, b) => (a.reihenfolge ?? 50) - (b.reihenfolge ?? 50) || a.label.localeCompare(b.label, "de"));
}

/** Reiter, die die angemeldete Person in dieser Kapsel sieht. RLS schützt die Daten trotzdem. */
export function useKapselReiter(lage: KapselLage): KapselReiter[] {
  const alle = useContext(KapselReiterKontext);
  const { rechte } = useAngemeldet();
  const { vorhabenId, name, meineRolle, aktiv, art } = lage;
  return useMemo(() => kapselReiterFuer(alle, rechte, { vorhabenId, name, meineRolle, aktiv, art }), [alle, rechte, vorhabenId, name, meineRolle, aktiv, art]);
}

/** Kennzahlen aller Module für die Karten und die Liste unter /vorhaben (BAUPLAN-FLAECHEN.md, Abschnitt 4.2), ungefiltert. Die Seite
 *  filtert je Vorhaben mit kapselReiterFuer, weil die Rolle je Vorhaben verschieden ist. */
export const KapselKennzahlenKontext = createContext<readonly KapselKennzahl[]>([]);

export function kapselKennzahlenAus(module: readonly Modul[]): KapselKennzahl[] {
  return module.flatMap((m) => m.kapselKennzahlen ?? []);
}

export function useKapselKennzahlen(): readonly KapselKennzahl[] {
  return useContext(KapselKennzahlenKontext);
}

/** Vorschau einer Unterlage (Paket E2). Ein Modul bringt sie mit (aufgaben), andere öffnen sie (Budget), ohne es zu importieren. */
export const UnterlageVorschauKontext = createContext<ComponentType<UnterlageVorschauProps> | null>(null);

export function unterlageVorschauAus(module: readonly Modul[]): ComponentType<UnterlageVorschauProps> | null {
  return module.find((m) => m.unterlageVorschau)?.unterlageVorschau ?? null;
}

export function useUnterlageVorschau(): ComponentType<UnterlageVorschauProps> | null {
  return useContext(UnterlageVorschauKontext);
}
